/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * One legend per surface — R4.
 *
 * R4 forbids a tooltip per row: with chips on every row, per-row tooltips are both
 * repetitive and undiscoverable. The legend is the trade, so it carries the whole
 * explanation.
 *
 * It lists **only the chips present in this section**, and renders nothing when
 * there are none. A legend explaining marks nobody has is the same noise as chipping
 * every value, and it is what teaches an operator to stop reading legends.
 *
 * Sits at the top of the section's content rather than in its header: a legend in a
 * collapsed header explains rows nobody can see.
 */

import React from 'react';
import { Box, HStack, Text, Wrap } from '@chakra-ui/react';
import type { ValueOrigin } from './parameter-editor.adapters';
import {
  PROVENANCE_MEANING,
  INFERRED_DECLARATION_MEANING,
  UNCONFIRMED_GUESS_LABEL,
  UNCONFIRMED_GUESS_MEANING,
  hasInferredDeclaration,
  hasUnconfirmedGuess,
  provenanceLegendFor,
} from './provenance-presentation';
import { ProvenanceChip } from './ProvenanceChip';
import { EvidenceMark } from './EvidenceMark';

export interface ProvenanceLegendProps {
  /** Every row's origin in this section, including the unmarked ones. */
  values: Array<ValueOrigin | undefined>;
}

export const ProvenanceLegend: React.FC<ProvenanceLegendProps> = ({ values }) => {
  const present = provenanceLegendFor(values);
  // The `check` mark is a dimension of `detected`, never present without it — so it can only add a
  // line to a legend that already renders, and the early return below still holds.
  const guess = hasUnconfirmedGuess(values);
  // Likewise a dimension of `template` (`D40`), so it too only adds a line to a legend that renders.
  const inferred = hasInferredDeclaration(values);
  if (present.length === 0) return null;

  return (
    <Wrap gap={3} px={2} py={1.5} borderBottomWidth="1px" borderColor="border.subtle" role="note">
      {present.map((provenance) => (
        <HStack key={provenance} gap={1.5} align="center">
          <ProvenanceChip provenance={provenance} />
          <Text fontSize="11px" color="fg.muted">
            {PROVENANCE_MEANING[provenance]}
          </Text>
        </HStack>
      ))}
      {guess && (
        <HStack gap={1.5} align="center">
          <Box as="span" fontSize="10px" px={1.5} borderRadius="sm" bg="warning.tint" color="warning.ink">
            {UNCONFIRMED_GUESS_LABEL}
          </Box>
          <Text fontSize="11px" color="fg.muted">
            {UNCONFIRMED_GUESS_MEANING}
          </Text>
        </HStack>
      )}
      {inferred && (
        <HStack gap={1.5} align="center">
          <EvidenceMark focusable={false} />
          <Text fontSize="11px" color="fg.muted">
            {INFERRED_DECLARATION_MEANING}
          </Text>
        </HStack>
      )}
    </Wrap>
  );
};
